import React, { useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Form, Input, Select, Button, Icon, message } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import { GENRE_OPTIONS, MOOD_OPTIONS } from '../AlbumCreate/constant';
import type { ChangeEvent } from 'react';
import './index.scss';

const { TextArea } = Input;

interface Album {
  id: string;
  title: string;
  artist: string;
  tracks: number;
  cover: string;
  genres: string[];
  moods?: string[];
  caption?: string;
  upc?: string;
  releaseDate: string;
  status: 'Under Review' | 'Released' | 'Rejected';
}

interface EditFormData {
  albumName: string;
  coverArtPreview: string | null;
  artists: string[];
  genres: string[];
  moods: string[];
  caption: string;
  upc: string;
}

const EditAlbum: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [isCoverHovered, setIsCoverHovered] = useState(false);

  // 从 location.state 获取专辑信息
  const albumFromState = location.state?.album as Album | undefined;

  // 表单数据
  const [formData, setFormData] = useState<EditFormData>({
    albumName: albumFromState?.title || '',
    coverArtPreview: albumFromState?.cover || null,
    artists: albumFromState?.artist ? [albumFromState.artist] : [''],
    genres: albumFromState?.genres?.length ? albumFromState.genres : [''],
    moods: albumFromState?.moods?.length ? albumFromState.moods : [''],
    caption: albumFromState?.caption || '',
    upc: albumFromState?.upc || '',
  });

  // 处理返回
  const handleBack = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo, { state: location.state.returnState || {} });
      return;
    }
    navigate(ROUTES.myAlbums);
  };

  // 封面上传
  const handleCoverClick = () => coverInputRef.current?.click();

  const handleCoverChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        message.error('Please upload JPG or PNG image');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error('File size exceeds 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, coverArtPreview: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  // 艺术家操作
  const handleAddArtist = () => setFormData(prev => ({ ...prev, artists: [...prev.artists, ''] }));
  const handleArtistChange = (index: number, value: string) => {
    setFormData(prev => ({ ...prev, artists: prev.artists.map((a, i) => (i === index ? value : a)) }));
  };
  const handleRemoveArtist = (index: number) => {
    if (index > 0) setFormData(prev => ({ ...prev, artists: prev.artists.filter((_, i) => i !== index) }));
  };

  // 流派操作
  const handleGenreChange = (index: number, value: string) => {
    setFormData(prev => ({ ...prev, genres: prev.genres.map((g, i) => (i === index ? value : g)) }));
  };
  const handleAddGenre = () => setFormData(prev => ({ ...prev, genres: [...prev.genres, ''] }));
  const handleRemoveGenre = (index: number) => {
    if (formData.genres.length > 1) setFormData(prev => ({ ...prev, genres: prev.genres.filter((_, i) => i !== index) }));
  };

  // 情绪操作
  const handleMoodChange = (index: number, value: string) => {
    setFormData(prev => ({ ...prev, moods: prev.moods.map((m, i) => (i === index ? value : m)) }));
  };
  const handleAddMood = () => setFormData(prev => ({ ...prev, moods: [...prev.moods, ''] }));
  const handleRemoveMood = (index: number) => {
    if (formData.moods.length > 1) setFormData(prev => ({ ...prev, moods: prev.moods.filter((_, i) => i !== index) }));
  };

  // Caption 变更
  const handleCaptionChange = (value: string) => {
    if (value.length <= 500) {
      setFormData(prev => ({ ...prev, caption: value }));
    }
  };

  // 表单验证
  const isFormValid = () => {
    const validGenres = formData.genres.filter(g => g.trim() !== '');
    return (
      formData.albumName.trim() !== '' &&
      formData.coverArtPreview !== null &&
      formData.artists[0].trim() !== '' &&
      validGenres.length > 0
    );
  };

  // 保存
  const handleSave = () => {
    if (!isFormValid()) {
      message.warning('Please fill all required fields');
      return;
    }
    console.log('Save album:', { id, ...formData });
    message.success('Album saved');
    handleBack();
  };

  return (
    <div className="edit-album">
      {/* Header */}
      <div className="edit-album__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Edit Album</span>
        </button>
      </div>

      {/* Content */}
      <div className="edit-album__content">
        <div className="edit-album__card">
          <h2 className="edit-album__title">Album Information</h2>
          <Form form={form} layout="vertical" className="edit-album__form">
            {/* Album Name */}
            <Form.Item label="Album Name" required className="form-item">
              <Input
                value={formData.albumName}
                onChange={(e) => setFormData(prev => ({ ...prev, albumName: e.target.value }))}
                placeholder="Enter album name"
              />
            </Form.Item>

            {/* Cover Art */}
            <Form.Item label="Cover Art" required className="form-item">
              <input ref={coverInputRef} type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleCoverChange} style={{ display: 'none' }} />
              <div
                className={`cover-upload ${isCoverHovered ? 'cover-upload--hover' : ''}`}
                onClick={handleCoverClick}
                onMouseEnter={() => setIsCoverHovered(true)}
                onMouseLeave={() => setIsCoverHovered(false)}
              >
                {formData.coverArtPreview ? (
                  <>
                    <img src={formData.coverArtPreview} alt="Cover" className="cover-upload__preview" />
                    <div className="cover-upload__overlay">
                      <span>Change</span>
                    </div>
                  </>
                ) : (
                  <div className="cover-upload__placeholder">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="white">
                      <path d="M38 26H26V38H22V26H10V22H22V10H26V22H38V26Z" />
                    </svg>
                  </div>
                )}
              </div>
              <p className="form-item__hint">Min 1000x1000 pixels, JPG or PNG. Max 10 MB.</p>
            </Form.Item>

            {/* Artist */}
            <Form.Item label="Artist" required className="form-item">
              {formData.artists.map((artist, index) => (
                <div key={index} className="form-item__row">
                  <Input value={artist} onChange={(e) => handleArtistChange(index, e.target.value)} placeholder={index === 0 ? 'Artist Name' : 'Featured Artist'} />
                  {index > 0 && <button type="button" className="remove-btn" onClick={() => handleRemoveArtist(index)}><Icon style="Close" /></button>}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddArtist}><span>+</span> Add Featured Artist</button>
            </Form.Item>

            {/* Genre */}
            <Form.Item label="Genre" required className="form-item">
              {formData.genres.map((genre, index) => (
                <div key={index} className="form-item__row">
                  <Select value={genre || undefined} onChange={(value) => handleGenreChange(index, value as string)} placeholder="Select genre" options={GENRE_OPTIONS.filter(g => !formData.genres.includes(g.value) || g.value === genre)} style={{ width: '100%' }} />
                  {formData.genres.length > 1 && <button type="button" className="remove-btn" onClick={() => handleRemoveGenre(index)}><Icon style="Close" /></button>}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddGenre}><span>+</span> Add Genre</button>
            </Form.Item>

            {/* Mood */}
            <Form.Item label="Mood" className="form-item">
              {formData.moods.map((mood, index) => (
                <div key={index} className="form-item__row">
                  <Select value={mood || undefined} onChange={(value) => handleMoodChange(index, value as string)} placeholder="Select mood" options={MOOD_OPTIONS.filter(m => !formData.moods.includes(m.value) || m.value === mood)} style={{ width: '100%' }} />
                  {formData.moods.length > 1 && <button type="button" className="remove-btn" onClick={() => handleRemoveMood(index)}><Icon style="Close" /></button>}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddMood}><span>+</span> Add Mood</button>
            </Form.Item>

            {/* Caption */}
            <Form.Item
              label={
                <div className="form-label-with-counter">
                  <span>Caption</span>
                  <span className="counter">{formData.caption.length}/500</span>
                </div>
              }
              className="form-item form-item--full"
            >
              {TextArea && (
                <TextArea
                  value={formData.caption}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleCaptionChange(e.target.value)}
                  placeholder="Add a description or caption for your album..."
                  rows={4}
                />
              )}
            </Form.Item>

            {/* UPC */}
            <Form.Item label="UPC" className="form-item">
              <Input
                value={formData.upc}
                onChange={(e) => setFormData(prev => ({ ...prev, upc: e.target.value }))}
                placeholder="Universal Product Code (optional)"
              />
              <p className="form-item__hint">If you have a UPC, enter it here. Otherwise, we'll generate one for you.</p>
            </Form.Item>

            {/* Save Button */}
            <Button type="primary" onClick={handleSave} className="edit-album__submit">Save</Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EditAlbum;
